import {
  BooleanResponseResult,
  CheckEmailExistCommand,
  ConfigurationProxy,
  CreateOTPResultResponseResult,
  ErrorMessage,
  RegisterUserCompleteCommand,
  RegisterUserResponseResponseResult,
  RequestEmailOtpModel,
  SigninUserCommand,
  SigninUserResponseResponseResult,
  UserProxy,
} from '@/app/proxy/proxies';
import { SharedModule } from '@/app/shared/shared.module';
import { Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HeaderComponent } from '../shared/header/header.component';
import { RegisterModules } from './steps/register.module';
import { ExternalProviderType, RegisterationSteps } from '../shared/auth-enums';
import { extractBackendErrorMessage } from '../shared/auth-error.util';
import { animate, style, transition, trigger } from '@angular/animations';
import { AuthService } from '../shared/services/auth.service';
import { UnifiedSsoService } from '../shared/services/unified-sso.service';
import { SSOProvider } from '../shared/interfaces/sso.interface';
import { UserService } from '@/app/core/services/user.service';
import { MixpanelTrackerService, RegistrationMethod } from '@/app/core/analytics';
import { LanguageInitializerService } from '@/app/core/services/language-initializer.service';

@Component({
  selector: 'app-register',
  imports: [SharedModule, HeaderComponent, RegisterModules],
  providers: [UserProxy, ConfigurationProxy],
  templateUrl: './register.component.html',
  styleUrl: './register.component.less',
  animations: [
    trigger('stepTransition', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' })),
      ]),
    ]),
  ],
})
export class RegisterComponent implements OnInit {
  private readonly _mixpanelTracker = inject(MixpanelTrackerService);
  private readonly _languageInitializerService = inject(LanguageInitializerService);

  selectedLanguageGuid: string = '';

  somethingWentWrong: boolean = false;
  errorMessage: string = '';
  registering: boolean = false;
  registeringAfterProfession = false;
  professionRegistrationError = '';

  RegisterationSteps = RegisterationSteps;
  registerationStep: RegisterationSteps = RegisterationSteps.EMAIL;

  registerModal: RegisterUserCompleteCommand = new RegisterUserCompleteCommand();

  // Track the initial language shown at onboarding (before any user selection)
  private initialLanguage: string = '';
  // Track if user has selected a language different from initial
  private languageChanged: boolean = false;
  // Track registration method for final event
  private currentRegistrationMethod: RegistrationMethod = 'email';
  // Track if alternative contact was entered
  private alternativeContactEntered: boolean = false;
  // Track if profession was entered and its value
  private professionEntered: boolean = false;
  private professionValue: string | undefined = undefined;

  constructor(
    private _location: Location,
    private _router: Router,
    private _authService: AuthService,
    private _userProxy: UserProxy,
    private _unifiedSsoService: UnifiedSsoService,
    private _userService: UserService
  ) {}

  ngOnInit(): void {
    // Initialize SSO providers
    this.initializeSSOProviders();

    // Store initial language for tracking
    this.initialLanguage = this._languageInitializerService.getCurrentLanguageCode();
  }

  private async initializeSSOProviders(): Promise<void> {
    try {
      await this._unifiedSsoService.initializeAllProviders();
    } catch (error) {
      console.error('Failed to initialize SSO providers:', error);
    }
  }

  backButton() {
    this._location.back();
  }

  onSSOButtonClick(provider: string) {
    let ssoProvider: SSOProvider;
    let registrationMethod: RegistrationMethod;

    // Map provider string to SSO provider and registration method
    switch (provider) {
      case 'apple':
        ssoProvider = SSOProvider.APPLE;
        registrationMethod = 'Apple';
        break;
      case 'google':
        ssoProvider = SSOProvider.GOOGLE;
        registrationMethod = 'Google';
        break;
      case 'ms':
        ssoProvider = SSOProvider.MICROSOFT;
        registrationMethod = 'Microsoft';
        break;
      case 'facebook':
        ssoProvider = SSOProvider.FACEBOOK;
        // Facebook is not in the spec, using Google as fallback for type safety
        registrationMethod = 'Google';
        break;
      default:
        console.error('Unknown SSO provider:', provider);
        return;
    }

    // Check if provider is available
    if (!this._unifiedSsoService.isProviderAvailable(ssoProvider)) {
      console.error(`SSO provider ${ssoProvider} is not available`);
      return;
    }

    // Track Registration Started event for SSO registration
    this.currentRegistrationMethod = registrationMethod;
    const currentLanguage = this._languageInitializerService.getCurrentLanguageCode();
    this._mixpanelTracker.trackRegistrationStarted({
      'device language': navigator.language?.split('-')[0] || 'en',
      'language welcomed': this.initialLanguage || 'en',
      'language continued': currentLanguage || 'en',
      'registration method': registrationMethod,
    });

    // Authenticate with the provider
    this._unifiedSsoService.authenticate(ssoProvider).subscribe({
      next: (response) => {
        if (response.success && response.userData && response.token) {
          // Map SSO provider to external provider type for signin
          let externalProviderType: ExternalProviderType;
          switch (ssoProvider) {
            case SSOProvider.APPLE:
              externalProviderType = ExternalProviderType.Apple;
              break;
            case SSOProvider.GOOGLE:
              externalProviderType = ExternalProviderType.Google;
              break;
            case SSOProvider.MICROSOFT:
              externalProviderType = ExternalProviderType.Microsoft;
              break;
            case SSOProvider.FACEBOOK:
              externalProviderType = ExternalProviderType.Facebook;
              break;
            default:
              console.error('Unknown SSO provider for signin');
              return;
          }

          // Sign in with the external provider
          this._userProxy
            .user_Signin(
              new SigninUserCommand({
                email: response.userData?.email,
                phone: '',
                phoneCode: '',
                password: '',
                newPassword: undefined,
                externalProviderType: externalProviderType,
                externalProviderToken: response.token,
                captchaToken: undefined,
              })
            )
            .subscribe({
              next: (res: SigninUserResponseResponseResult) => {
                this._authService.handleSigninResponse(res);
                
                this._userService.loadUserProfile().subscribe({
                  next: () => {
                    this._authService.handlePostLoginRedirect();
                  },
                  error: () => {
                    this._authService.handlePostLoginRedirect();
                  }
                });
              },
              error: (error) => {
                console.error(`SSO signin error for ${ssoProvider}:`, error);
              }
            });
        } else {
          console.error('SSO authentication failed:', response.error);
        }
      },
      error: (error) => {
        console.error(`SSO authentication error for ${ssoProvider}:`, error);
      },
    });
  }

  goToLogin() {
    // Navigate to login page
    this._router.navigate(['/auth/login']);
  }

  goToRegister() {
    if (this.registering || !this.registerModal.email || this.somethingWentWrong) {
      return;
    }

    this.registering = true;
    let input = new CheckEmailExistCommand();
    input.email = this.registerModal.email;
    this._userProxy.user_CheckEmailExist(input).subscribe({
      next: (res: BooleanResponseResult) => {
        if (!res.result) {
          this.somethingWentWrong = false;
          this.errorMessage = '';

          // Consent is given by continuing past the terms notice on the welcome screen
          //this.registerModal.acceptConsent = true;

          // Track Registration Started event for email registration
          this.currentRegistrationMethod = 'email';
          const currentLanguage = this._languageInitializerService.getCurrentLanguageCode();
          this._mixpanelTracker.trackRegistrationStarted({
            'device language': navigator.language?.split('-')[0] || 'en',
            'language welcomed': this.initialLanguage || 'en',
            'language continued': currentLanguage || 'en',
            'registration method': 'email',
          });

          this.requestEmailOtp();
        } else {
          this.somethingWentWrong = true;
          this.errorMessage = 'doktar_app.register.email_is_exist_error';
          this.registering = false;
        }
      },
      error: () => {
        this.registering = false;
      },
    });
  }

  /**
   * Request the registration email OTP. When the backend reports that no OTP is
   * required, the verification step is skipped and the OTP is submitted as 0.
   */
  private requestEmailOtp() {
    this._userProxy
      .user_RequestEmailOtp(new RequestEmailOtpModel({ email: this.registerModal.email }))
      .subscribe({
        next: (res: CreateOTPResultResponseResult) => {
          if (!res.isSuccess || !res.result) {
            this.handleEmailOtpRequestFailure(res.errors);
            return;
          }

          const otpRequired = res.result.otpRequired;
          this.registerModal.emailOtp = otpRequired ? undefined : 0;
          this._authService.setRegisterModal(this.registerModal);

          this.registerationStep = otpRequired
            ? RegisterationSteps.VERIFY_EMAIL_OTP
            : RegisterationSteps.PASSWORD;
          this.registering = false;
        },
        error: () => this.handleEmailOtpRequestFailure(),
      });
  }

  private handleEmailOtpRequestFailure(errors?: ErrorMessage[]) {
    this.registering = false;
    this.somethingWentWrong = true;
    this.errorMessage = extractBackendErrorMessage(errors);
  }

  get phoneOtpTarget(): string {
    const registerModal = this._authService.getRegisterModal();
    return registerModal?.phone ? `+${registerModal.phoneCode} ${registerModal.phone}` : '';
  }

  onEmailChange(_event: any) {
    if (this.somethingWentWrong) {
      this.somethingWentWrong = false;
      this.errorMessage = '';
    }
  }

  onSelectedLanguageGuid(event: string) {
    this.selectedLanguageGuid = event;
  }

  /**
   * Handle profession selection from profession step
   */
  onProfessionSelected(event: { entered: boolean; value: string | undefined }) {
    this.professionEntered = event.entered;
    this.professionValue = event.value;
  }

  /**
   * Create the account only after profession was entered or skipped.
   * The profession step itself cannot register, because the user does not exist yet.
   */
  registerAfterProfession() {
    if (this.registeringAfterProfession) {
      return;
    }

    const registerModal = this._authService.getRegisterModal();
    if (!registerModal) {
      this.failProfessionRegistration();
      return;
    }

    this.registeringAfterProfession = true;
    this.professionRegistrationError = '';

    this._userProxy.user_Register(this.buildRegisterCommand(registerModal)).subscribe({
      next: (res: RegisterUserResponseResponseResult) => {
        if (res.isSuccess && res.result?.isSuccess) {
          this._userService.setRegistrationUserId(res.result.id);
          this.signInAfterRegistration(registerModal);
          return;
        }

        this.failProfessionRegistration(res.errors);
      },
      error: () => this.failProfessionRegistration(),
    });
  }

  /**
   * A phone code is absent when the step was skipped or when the backend reported
   * that no phone verification is required; the register endpoint expects an
   * explicit null for those registrations.
   */
  private buildRegisterCommand(registerModal: RegisterUserCompleteCommand): RegisterUserCompleteCommand {
    const command = new RegisterUserCompleteCommand(registerModal.toJSON());
    command.emailOtp = registerModal.emailOtp ?? 0;
    command.phoneOtp = registerModal.phoneOtp ?? (null as unknown as number);
    return command;
  }

  private signInAfterRegistration(registerModal: RegisterUserCompleteCommand) {
    const userSignInReq = new SigninUserCommand({
      email: registerModal.email,
      phone: registerModal.phone,
      phoneCode: registerModal.phoneCode,
      captchaToken: '',
      password: registerModal.password,
      newPassword: undefined,
      externalProviderToken: '',
      externalProviderType: '',
    });

    this._userProxy.user_Signin(userSignInReq).subscribe({
      next: (res: SigninUserResponseResponseResult) => {
        if (res.isSuccess) {
          this._authService.handleSigninResponse(res);
          this.finishRegistration();
          return;
        }

        this.failProfessionRegistration(res.errors);
      },
      error: () => this.failProfessionRegistration(),
    });
  }

  private failProfessionRegistration(errors?: ErrorMessage[]) {
    this.registeringAfterProfession = false;
    this.professionRegistrationError = extractBackendErrorMessage(errors);
  }

  private finishRegistration() {
    const currentLanguage = this._languageInitializerService.getCurrentLanguageCode();
    const userId = localStorage.getItem('userId') || '';
    const registerModal = this._authService.getRegisterModal();
    this._mixpanelTracker.trackRegistrationCompleted(
      {
        'registration method': this.currentRegistrationMethod,
        'alternative contact entered': this.alternativeContactEntered,
        'profession entered': this.professionEntered,
        profession: this.professionValue,
      },
      {
        'user id': userId,
        'registration method': this.currentRegistrationMethod,
        'registration date': new Date().toISOString(),
        language: currentLanguage || 'en',
        profession: this.professionValue,
        'has alternative contact': this.alternativeContactEntered,
        'phone number country': registerModal?.phoneCode || undefined,
      }
    );

    this._userService.loadUserProfile().subscribe({
      next: () => {
        this._authService.handlePostLoginRedirect();
      },
      error: (error) => {
        console.error('Failed to load user profile after registration:', error);
        this._authService.handlePostLoginRedirect();
      },
    });
  }

  onSteperChange(event: RegisterationSteps) {
    this.registerationStep = event;

    // Track alternative contact and profession entry based on step transitions
    if (event === RegisterationSteps.PROFESSION) {
      // If we're moving to profession step, user entered phone (alternative contact)
      const registerModal = this._authService.getRegisterModal();
      if (registerModal?.phone) {
        this.alternativeContactEntered = true;
      }
    }

    setTimeout(() => {
      this.registerationStep = event;
    }, 100);
  }
}
